// Builds Google Maps links from a stored event location.
//
// COPY of utils/locationLink.js at the repo root. Create React App cannot import
// from outside its own src folder, so the helper lives twice. Keep both in sync;
// tests/locationLink.test.js runs both copies through the same cases.
//
// See the root copy for the full explanation of why the old
// /maps/place/?q=place_id:XXX format was dropped.

const MAPS_SEARCH_BASE = 'https://www.google.com/maps/search/'
const ABSOLUTE_URL = /^https?:\/\//i

// Matches the broken URL this app saved between April 2026 and this fix. Those
// rows are still in the database, so we pull the place ID back out and rebuild
// the link in the supported format instead of passing the bad URL through.
const LEGACY_PLACE_ID_URL = /[?&]q=place_id:([^&#\s]+)/i

const clean = value => (typeof value === 'string' ? value.trim() : '')

const mapsSearch = (query, placeId) => {
    const params = new URLSearchParams({ api: '1', query })
    if (placeId) params.set('query_place_id', placeId)
    return `${MAPS_SEARCH_BASE}?${params.toString()}`
}

// Builds a map link for the given location text, pinned to placeId when known.
// Returns '' when there is no location, so callers can skip rendering a link.
function buildLocationUrl(location, placeId) {
    const address = clean(location)
    if (!address) return ''

    const id = clean(placeId)

    if (ABSOLUTE_URL.test(address)) {
        const legacy = address.match(LEGACY_PLACE_ID_URL)
        // No address was stored alongside these rows, so the place ID doubles as
        // the query text. Google resolves query_place_id first, so the map still
        // lands on the right place.
        if (legacy) {
            const legacyId = id || decodeURIComponent(legacy[1])
            return mapsSearch(legacyId, legacyId)
        }
        // Any other stored URL (an old share link, or the AGM Google Meet link)
        // already works. Leave it exactly as it is.
        return address
    }

    return mapsSearch(address, id)
}

// The text to show for the link. Prefers the address itself, so a reader can
// still find the place if the link fails or the map app is missing. Legacy rows
// hold only a URL and have no address to show, so those get generic wording.
function locationLabel(location, fallback = 'Open map') {
    const address = clean(location)
    if (!address) return ''
    return ABSOLUTE_URL.test(address) ? fallback : address
}

module.exports = { buildLocationUrl, locationLabel }
